/**
 * ProgressEntryForm Component
 * Quick form for logging goal progress
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState } from 'react';
import { X, Save, Loader2, TrendingUp, Calendar, FileText } from 'lucide-react';
import type { ProgressEntryFormData, GoalMeasurementType } from '@/features/goals/types/goalsTypes';

interface ProgressEntryFormProps {
  goalId: string;
  measurementType: GoalMeasurementType;
  targetValue?: string | null;
  onSubmit: (data: ProgressEntryFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const SESSION_TYPES = ['Therapy', 'School', 'Home', 'Medical', 'Other'];

export function ProgressEntryForm({
  goalId,
  measurementType,
  targetValue,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ProgressEntryFormProps) {
  const [formData, setFormData] = useState<ProgressEntryFormData>({
    entryDate: new Date().toISOString().split('T')[0],
    value: '',
    percentage: 0,
    notes: '',
    sessionType: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
    setFormData((prev) => ({ ...prev, percentage: value }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.entryDate) newErrors.entryDate = 'Date is required';
    if (!formData.value && measurementType !== 'yes_no') {
      newErrors.value = 'Value is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(formData);
  };

  const renderValueInput = () => {
    switch (measurementType) {
      case 'percentage':
        return (
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Progress (%) <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                name="percentage"
                value={formData.percentage}
                onChange={handlePercentageChange}
                min="0"
                max="100"
                className="flex-1 h-2 bg-cream rounded-lg appearance-none cursor-pointer"
              />
              <input
                type="number"
                name="value"
                value={formData.percentage}
                onChange={(e) => {
                  const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                  setFormData((prev) => ({ ...prev, percentage: val, value: String(val) }));
                }}
                min="0"
                max="100"
                className="w-20 px-3 py-2 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage text-center"
              />
              <span className="text-warm-gray">%</span>
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Achieved? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {['Yes', 'No'].map((option) => (
                <label key={option} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="value"
                    value={option}
                    checked={formData.value === option}
                    onChange={handleChange}
                    className="w-4 h-4 text-sage focus:ring-sage"
                  />
                  <span className="text-navy">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'rating':
        return (
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Rating (1-5) <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setFormData((prev) => ({ 
                    ...prev, 
                    value: String(rating),
                    percentage: rating * 20 
                  }))}
                  className={`w-12 h-12 rounded-lg border-2 font-semibold transition-colors ${
                    formData.value === String(rating)
                      ? 'bg-sage text-white border-sage'
                      : 'bg-cream text-navy border-transparent hover:border-sage-light'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Value <span className="text-red-500">*</span>
              {targetValue && <span className="text-warm-gray font-normal"> (Target: {targetValue})</span>}
            </label>
            <input
              type="text"
              name="value"
              value={formData.value}
              onChange={handleChange}
              placeholder="Enter current value..."
              className={`w-full px-4 py-3 bg-cream border-2 rounded-lg focus:outline-none transition-colors ${
                errors.value ? 'border-red-500' : 'border-transparent focus:border-sage'
              }`}
            />
            {errors.value && <p className="mt-1 text-sm text-red-500">{errors.value}</p>}
          </div>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-center justify-between pb-4 border-b border-warm-gray/10">
        <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-sage" />
          Log Progress
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-warm-gray hover:text-navy hover:bg-cream rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Date */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Date <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <input
            type="date"
            name="entryDate"
            value={formData.entryDate}
            onChange={handleChange}
            className={`w-full pl-10 pr-4 py-3 bg-cream border-2 rounded-lg focus:outline-none transition-colors ${
              errors.entryDate ? 'border-red-500' : 'border-transparent focus:border-sage'
            }`}
          />
        </div>
        {errors.entryDate && <p className="mt-1 text-sm text-red-500">{errors.entryDate}</p>}
      </div>

      {/* Value Input */}
      {renderValueInput()}

      {/* Session Type */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Session Type</label>
        <select
          name="sessionType"
          value={formData.sessionType}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage transition-colors"
        >
          <option value="">Select session type...</option>
          {SESSION_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Notes</label>
        <div className="relative">
          <FileText className="absolute left-3 top-3 w-5 h-5 text-warm-gray" />
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Any observations or notes..."
            rows={3}
            className="w-full pl-10 pr-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage transition-colors resize-none"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-warm-gray/10">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Progress
        </button>
      </div>
    </form>
  );
}

export default ProgressEntryForm;

